import React from 'react';
import { motion } from 'framer-motion';
import { Users, Target, Heart, Award, Sparkles, TrendingUp } from 'lucide-react';
import Header from '../components/Header';
import Footer from '../components/Footer';
import { Button } from '../components/ui/button';
import { useDemoModal } from '../context/DemoModalContext';

const AboutUs = () => {
  const { openDemoModal } = useDemoModal();
  
  const values = [
    {
      icon: Heart,
      title: 'Student-Centric Approach',
      description: 'Every decision we make prioritizes student success and learning outcomes',
      color: 'from-red-400 to-pink-500'
    },
    {
      icon: Target,
      title: 'Excellence in Teaching',
      description: 'We maintain the highest standards in tutor selection and training',
      color: 'from-blue-400 to-cyan-500'
    },
    {
      icon: Users,
      title: 'Inclusive Learning',
      description: 'Quality education accessible to students from all backgrounds',
      color: 'from-green-400 to-emerald-500'
    },
    {
      icon: Sparkles,
      title: 'Innovation First',
      description: 'Constantly evolving our methods with latest educational technology',
      color: 'from-purple-400 to-pink-500'
    }
  ];

  const stats = [
    { number: '1000+', label: 'Students Empowered' },
    { number: '100+', label: 'Expert Tutors' },
    { number: '50,000+', label: 'Sessions Completed' },
    { number: '99%', label: 'Student Satisfaction' }
  ];

  const team = [
    {
      name: 'Sapna Sinha',
      role: 'Founder & CEO',
      image: 'https://customer-assets.emergentagent.com/job_ce117f76-c768-4b53-b33a-c699a14d312a/artifacts/i5b6417d_Sapna%20Sinha.avif',
      bio: 'An Educator with 15+ years in education'
    },
    {
      name: 'Priyanka Lincon',
      role: 'HR Manager',
      image: 'https://images.unsplash.com/photo-1580489944761-15a19d654956',
      bio: 'Building and nurturing our talented team'
    },
    {
      name: 'Jagat Bhushan',
      role: 'Head of Technology',
      image: 'https://customer-assets.emergentagent.com/job_ce117f76-c768-4b53-b33a-c699a14d312a/artifacts/as4eg4kc_jagat_photo.avif',
      bio: 'EdTech innovator building cutting-edge learning platforms'
    }
  ];

  return (
    <div className="min-h-screen bg-white">
      <Header />

      {/* Hero Section */}
      <section className="pt-32 pb-20 bg-gradient-to-br from-cyan-600 via-blue-600 to-teal-600 relative overflow-hidden">
        <div className="absolute inset-0 overflow-hidden opacity-20">
          <motion.div
            className="absolute top-0 right-0 w-96 h-96 bg-white rounded-full filter blur-3xl"
            animate={{ scale: [1, 1.2, 1], x: [0, 50, 0] }}
            transition={{ duration: 10, repeat: Infinity }}
          />
        </div>

        <div className="container mx-auto px-6 lg:px-12 relative z-10">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="max-w-4xl mx-auto text-center text-white"
          >
            <h1 className="text-5xl lg:text-6xl font-bold mb-6">
              Transforming Education, One Student at a Time
            </h1>
            <p className="text-xl text-cyan-100 mb-8">
              At FourQ Learn, we believe every student deserves personalized attention and expert guidance to reach their full potential.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Story Section */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-6 lg:px-12">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
            >
              <h2 className="text-4xl font-bold text-gray-900 mb-6">
                Our Story
              </h2>
              <div className="space-y-4 text-gray-600 leading-relaxed">
                <p>
                  Founded in 2020, FourQ Learn was born from a simple observation: traditional classroom settings don't work for every student. Our founder, Sapna Sinha, a Teacher, witnessed countless bright students struggling not because they lacked potential, but because they needed personalized attention.
                </p>
                <p>
                  This insight led to the creation of the revolutionary FourQ Method – a four-quadrant teaching approach that adapts to each student's unique learning style. What started as a small tutoring center in Bengaluru has now grown into India's most loved online tutoring platform.
                </p>
                <p>
                  Today, we're proud to serve over 1000 students across India, with a network of 100+ expert tutors who share our passion for educational excellence.
                </p>
              </div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              className="relative"
            >
              <img
                src="https://images.unsplash.com/photo-1522202176988-66273c2fd55f"
                alt="FourQ Learn Team"
                className="rounded-3xl shadow-2xl w-full"
              />
            </motion.div>
          </div>
        </div>
      </section>

      {/* Stats Section */}
      <section className="py-20 bg-gradient-to-br from-cyan-50 to-blue-50">
        <div className="container mx-auto px-6 lg:px-12">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-12">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">
              Our Impact in Numbers
            </h2>
          </motion.div>

          <div className="grid md:grid-cols-4 gap-8">
            {stats.map((stat, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                className="bg-white rounded-2xl p-8 text-center shadow-lg"
              >
                <div className="text-4xl font-bold text-cyan-600 mb-2">{stat.number}</div>
                <div className="text-gray-600">{stat.label}</div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Values Section */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-6 lg:px-12">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-16">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">
              Our Core Values
            </h2>
            <p className="text-xl text-gray-600">The principles that guide everything we do</p>
          </motion.div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            {values.map((value, index) => {
              const Icon = value.icon;
              return (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, y: 30 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: index * 0.1 }}
                  className="bg-white rounded-2xl p-8 shadow-lg hover:shadow-xl transition-all duration-300 border border-gray-100"
                >
                  <div className={`w-16 h-16 bg-gradient-to-br ${value.color} rounded-2xl flex items-center justify-center mb-6`}>
                    <Icon className="w-8 h-8 text-white" />
                  </div>
                  <h3 className="text-xl font-bold text-gray-900 mb-3">{value.title}</h3>
                  <p className="text-gray-600">{value.description}</p>
                </motion.div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Team Section */}
      <section className="py-20 bg-gradient-to-br from-gray-50 to-cyan-50">
        <div className="container mx-auto px-6 lg:px-12">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-16">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">
              Meet Our Leadership Team
            </h2>
            <p className="text-xl text-gray-600">Passionate educators and innovators driving our mission</p>
          </motion.div>

          <div className="grid md:grid-cols-3 gap-8 max-w-5xl mx-auto">
            {team.map((member, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.2 }}
                className="bg-white rounded-2xl p-6 shadow-lg text-center"
              >
                <img
                  src={member.image}
                  alt={member.name}
                  className="w-32 h-32 rounded-full object-cover mx-auto mb-4 border-4 border-cyan-200"
                />
                <h3 className="text-xl font-bold text-gray-900 mb-1">{member.name}</h3>
                <p className="text-cyan-600 font-medium mb-3">{member.role}</p>
                <p className="text-gray-600 text-sm">{member.bio}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-gradient-to-r from-cyan-600 to-blue-600">
        <div className="container mx-auto px-6 lg:px-12 text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <h2 className="text-4xl font-bold text-white mb-6">
              Join Us in Transforming Education
            </h2>
            <p className="text-xl text-cyan-100 mb-8 max-w-2xl mx-auto">
              Whether you're a student seeking excellence or a tutor passionate about teaching, there's a place for you at FourQ Learn.
            </p>
            <div className="flex flex-wrap justify-center gap-4">
              <Button size="lg" className="bg-white text-cyan-600 hover:bg-gray-50 px-8 py-6 text-lg" onClick={openDemoModal}>
                Book Free Session
              </Button>
              <Button size="lg" variant="outline" className="border-2 border-white text-white hover:bg-white/10 px-8 py-6 text-lg">
                Become a Tutor
              </Button>
            </div>
          </motion.div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default AboutUs;